import React, { useState } from 'react';
import { ArrowLeftIcon, ScaleIcon, CheckCircleIcon, SearchIcon, FilterIcon, ClockIcon } from 'lucide-react';
import { WeighingModule } from './WeighingModule';
interface WeighingOverviewProps {
  onBackToOverview?: () => void;
}
interface WeighingTask {
  id: string;
  materialId: string;
  materialName: string;
  orderId: string;
  orderName: string;
  timestamp: Date;
  status: 'pending' | 'completed';
  targetWeight: number;
  unit: string;
}
export function WeighingOverview({
  onBackToOverview
}: WeighingOverviewProps) {
  // State für die Verwiegungsaufträge
  const [weighingTasks, setWeighingTasks] = useState<WeighingTask[]>([{
    id: 'W001',
    materialId: 'R061307',
    materialName: 'MSC Alaska Seelachs',
    orderId: '1001',
    orderName: 'Gustoria Paella',
    timestamp: new Date(new Date().getTime() - 15 * 60000),
    status: 'pending',
    targetWeight: 25.5,
    unit: 'kg'
  }, {
    id: 'W002',
    materialId: 'R053408',
    materialName: 'Hähnchen Brust St gebraten',
    orderId: '1001',
    orderName: 'Gustoria Paella',
    timestamp: new Date(new Date().getTime() - 10 * 60000),
    status: 'pending',
    targetWeight: 15.3,
    unit: 'kg'
  }]);
  // State für die Filteroption
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'completed'>('all');
  // State für die Suche
  const [searchTerm, setSearchTerm] = useState('');
  // State für den aktiven Verwiegungsauftrag
  const [activeTask, setActiveTask] = useState<WeighingTask | null>(null);
  // Gefilterte und durchsuchte Aufträge
  const filteredTasks = weighingTasks.filter(task => {
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesSearch = task.materialName.toLowerCase().includes(searchTerm.toLowerCase()) || task.materialId.toLowerCase().includes(searchTerm.toLowerCase()) || task.orderName.toLowerCase().includes(searchTerm.toLowerCase()) || task.orderId.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });
  // Handler für Verwiegung abschließen
  const handleWeighingComplete = () => {
    if (activeTask) {
      // Aktualisiere den Status des Auftrags
      setWeighingTasks(tasks => tasks.map(task => task.id === activeTask.id ? {
        ...task,
        status: 'completed'
      } : task));
      // Zurück zur Übersicht
      setActiveTask(null);
    }
  };
  // Wenn ein aktiver Auftrag ausgewählt ist, zeige die Verwiegungsmaske
  if (activeTask) {
    return <WeighingModule materialId={activeTask.materialId} orderId={activeTask.orderId} onBack={() => setActiveTask(null)} onComplete={handleWeighingComplete} data-id="element-2670" />;
  }
  return <div className="container mx-auto" data-id="element-2671">
      <div className="mb-8" data-id="element-2672">
        <div data-id="element-2673">
          <h2 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-2674">Verwiegung</h2>
          <p className="text-gray-600 mb-4" data-id="element-2675">
            Verwiegung und Kontrolle von Rohstoffen
          </p>
          {onBackToOverview}
        </div>
      </div>
      {weighingTasks.length === 0 ? <div className="bg-white rounded-xl shadow-md p-8 text-center" data-id="element-2676">
          <div className="p-6 mx-auto" data-id="element-2677">
            <ScaleIcon size={64} className="text-gray-400 mx-auto mb-4" data-id="element-2678" />
            <h3 className="text-xl font-medium text-gray-900 mb-2" data-id="element-2679">
              Aktuell sind keine Verwiegungen geplant
            </h3>
            <p className="text-gray-500 max-w-md mx-auto" data-id="element-2680">
              Es wurden keine anstehenden Verwiegungsaufträge gefunden. Neue
              Verwiegungsaufträge werden hier angezeigt, sobald sie geplant
              sind.
            </p>
          </div>
        </div> : <div className="bg-white rounded-xl shadow-md p-6" data-id="element-2681">
          <div className="mb-6" data-id="element-2682">
            <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-2683">
              Anstehende Verwiegungen
            </h3>
            {/* Filter- und Suchleiste */}
            <div className="flex flex-col md:flex-row gap-4 mb-6" data-id="element-2684">
              <div className="relative flex-grow" data-id="element-2685">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2686">
                  <SearchIcon size={18} className="text-gray-400" data-id="element-2687" />
                </div>
                <input type="text" className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md" placeholder="Nach Material oder Auftrag suchen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2688" />
              </div>
              <div className="flex items-center" data-id="element-2689">
                <FilterIcon size={18} className="text-gray-500 mr-2" data-id="element-2690" />
                <select className="p-2 border border-gray-300 rounded-md" value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} data-id="element-2691">
                  <option value="all" data-id="element-2692">Alle Aufträge</option>
                  <option value="pending" data-id="element-2693">Ausstehend</option>
                  <option value="completed" data-id="element-2694">Abgeschlossen</option>
                </select>
              </div>
            </div>
            {/* Liste der Verwiegungsaufträge */}
            <div className="space-y-4" data-id="element-2695">
              {filteredTasks.length > 0 ? filteredTasks.map(task => <div key={task.id} className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer
                      ${task.status === 'completed' ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`} onClick={() => task.status === 'pending' && setActiveTask(task)} data-id="element-2696">
                    <div className="flex justify-between items-start" data-id="element-2697">
                      <div data-id="element-2698">
                        <h4 className="font-medium text-gray-900" data-id="element-2699">
                          {task.materialName}
                        </h4>
                        <p className="text-sm text-gray-500" data-id="element-2700">
                          Material-Nr: {task.materialId}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-2701">
                          Auftrag: #{task.orderId} - {task.orderName}
                        </p>
                        <div className="flex items-center text-sm text-gray-500 mt-1" data-id="element-2702">
                          <ClockIcon size={14} className="mr-1" data-id="element-2703" />
                          <span data-id="element-2704">
                            Erstellt: {task.timestamp.toLocaleTimeString()} Uhr
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end" data-id="element-2705">
                        <div className="text-lg font-semibold text-gray-900" data-id="element-2706">
                          {task.targetWeight} {task.unit}
                        </div>
                        {task.status === 'completed' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1" data-id="element-2707">
                            <CheckCircleIcon size={12} className="mr-1" data-id="element-2708" />
                            Abgeschlossen
                          </span> : <button className="mt-2 px-3 py-1 bg-[#8DC63F] hover:bg-[#7AB929] text-white rounded-md text-sm" onClick={() => setActiveTask(task)} data-id="element-2709">
                            Jetzt verwiegen
                          </button>}
                      </div>
                    </div>
                  </div>) : <div className="text-center py-8 bg-gray-50 rounded-lg" data-id="element-2710">
                  <ScaleIcon size={40} className="mx-auto text-gray-400 mb-3" data-id="element-2711" />
                  <h4 className="text-lg font-medium text-gray-900 mb-1" data-id="element-2712">
                    Keine Verwiegungen gefunden
                  </h4>
                  <p className="text-gray-500" data-id="element-2713">
                    Es wurden keine Verwiegungsaufträge gefunden, die den
                    Filterkriterien entsprechen.
                  </p>
                </div>}
            </div>
          </div>
        </div>}
    </div>;
}